import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import ExtensionInputGroup from "./ExtensionInputGroup";
import Options from "app/popleads/components/ui-kit/Options";
import ExtensionNewInhouseDeal from "./ExtensionNewInhouseDeal";
import { useExtensionContext } from "../ExtensionContext";
import { campaignTypesOptions, DEAL_CANCELLED, DEAL_LOST, DEAL_STATUS_OPTIONS, DEAL_WON } from "../utils/constants";
import ExtensionNewPublicDeal from "./ExtensionNewPublicDeal";
import ExtensionConsultingDeal from "./ExtensionConsultingDeal";
import { colors } from "app/popleads/components/ui-kit/colors";
import useCompanyData from "app/popleads/hooks/useCompanyData";
import { toast } from "react-toastify";
import { publicTypes } from "../utils/constants";

export const StyledLabel = styled.label`
    display: block;
    font-size: 12px;
    color: ${colors.neutral[600]};
    margin-bottom: 0px;

    ${(props) =>
        props.required &&
        `
    &:after {
      content: '*';
      color: #ff4d4f;
      margin-left: 4px;
    }
  `}

    ${(props) =>
        props.disabled &&
        `
    color: #999;
    cursor: not-allowed;
  `}
`;

const ExtensionNewDeal = forwardRef((props, ref) => {
    const { setDealObject, dealObject: contextDealObject, profile } = useExtensionContext();
    const { companyUsers } = useCompanyData();

    const [dealObject, setLocalDealObject] = useState(contextDealObject);
    useEffect(() => {
        setLocalDealObject(contextDealObject);
    }, [contextDealObject]);

    useEffect(() => {
        if (dealObject && Object.keys(dealObject).length > 0) {
            if (dealObject.dealPotential === undefined && dealObject.dealCampaignType) {
                const mappedDealObject = {
                    ...dealObject,
                    campaignId: dealObject?.campaign?.id || "",
                    campaignType: dealObject?.campaign?.type || "",
                    campaign: dealObject.campaign || "",
                    status:
                        dealObject?.status === DEAL_WON || dealObject?.durum === DEAL_WON
                            ? DEAL_WON
                            : dealObject.status || "",
                    dealPotential:
                        dealObject?.status === DEAL_WON || dealObject?.durum === DEAL_WON
                            ? 75
                            : dealObject.lastStatus
                            ? parseInt(dealObject.lastStatus.replace("%", ""))
                            : 25,
                    amount: dealObject.amount || "",
                    numberOfUnits: dealObject.units || 1,
                    type: dealObject.type || "",
                    channelType: dealObject.typeChannel || dealObject.dealTypeChannel || "",
                    certificateName: dealObject.certificateName || "",
                    certificateSurname: dealObject.certificateLastName || "",
                    courseName: dealObject.courseName || "",
                    accountName: dealObject.accountName || "",
                    partner: dealObject?.partner || "",
                    vatIncludes: dealObject.vatIncludes ? "Yes" : "No",
                    paidDeal: dealObject.paid ? "Yes" : "No",
                    paidAmount: dealObject.paidAmount || "",
                    isSelfInvestment: dealObject.isSelfInvestment === 1 ? "Yes" : "No",
                    departmentName: dealObject.departmentName || "",
                    selectedInquiry: dealObject.inquiryActivityId
                        ? {
                              activityId: dealObject.inquiryActivityId,
                              profilId: dealObject.profilId || profile?.id,
                          }
                        : null,
                    closingDate: dealObject.kapanmaTarihi || "",
                };
                setDealObject(mappedDealObject);
            }
        }
    }, [dealObject, setDealObject, companyUsers, profile]);

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case "amount":
                if (!value) {
                    toast.warning("You need to enter the amount first");
                    return true;
                }
                if (dealObject?.campaign?.type === "Public-ExEd" && value > dealObject.campaign.fullPrice) {
                    toast.warning("Deal amount can not be higher than campaign full price!");
                    return false;
                }
                if (isNaN(value) || value <= 0) {
                    toast.warning("Amount must be a positive number");
                    return false;
                }
                break;
            case "campaign":
                if (!value && publicTypes.includes(dealObject.dealCampaignType)) {
                    toast.warning("You need to enter the campaign first");
                    // return false;
                }
                break;
            case "closingDate":
                if (!value && [DEAL_WON, DEAL_LOST, DEAL_CANCELLED].includes(dealObject.status)) {
                    toast.warning("Please choose a closing date");
                    return false;
                }
                break;
            case "type":
                if (!value && dealObject.status === DEAL_WON) {
                    toast.warning("You need to enter the type first");
                    return false;
                }
                break;
            case "channelType":
                if (!value && ["Marketing", "Marketing Cross Sale"].includes(dealObject.type)) {
                    toast.warning("Please select marketing channel");
                    return false;
                }
                break;
            case "certificateName":
            case "certificateLastName":
                if (
                    !value &&
                    ["Public", "LVT Public", "L&D Broker", "Public-ExEd"].includes(dealObject.dealCampaignType) &&
                    dealObject.status === DEAL_WON
                ) {
                    toast.warning(
                        `Please provide a certificate ${fieldName === "certificateName" ? "name" : "lastname"}`
                    );
                    return false;
                }
                // L&D Broker ve Public-ExEd için tüm statülerde zorunlu
                if (!value && ["L&D Broker", "Public-ExEd"].includes(dealObject.dealCampaignType)) {
                    toast.warning(
                        `Please provide a certificate ${fieldName === "certificateName" ? "name" : "lastname"}`
                    );
                    return false;
                }
                break;
            default:
                return true;
        }
        return true;
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {dealObject?.toBe !== DEAL_WON && (
                <>
                    <ExtensionInputGroup
                        label="Select the Deal Potential"
                        type="range"
                        options={{ min: 25, max: 75, step: 25 }}
                        onChange={(value) => {
                            if (dealObject.dealPotential === 75 && value !== 75 && dealObject.status === DEAL_WON) {
                                setDealObject({ ...dealObject, status: "", dealPotential: value });
                            } else {
                                setDealObject({ ...dealObject, dealPotential: value });
                            }
                        }}
                        value={dealObject.dealPotential}
                        disabled={dealObject?.toBe === DEAL_WON}
                    />

                    <div className="d-flex align-items-center justify-content-between">
                        <StyledLabel>Status</StyledLabel>
                        <div className="d-flex" style={{ gap: "8px" }}>
                            <Options
                                options={
                                    dealObject.dealPotential <= 50 ? DEAL_STATUS_OPTIONS.slice(1) : DEAL_STATUS_OPTIONS
                                }
                                selected={dealObject.status}
                                setSelected={(value) => {
                                    if (value === dealObject.status) {
                                        setDealObject({ ...dealObject, status: "" });
                                    } else {
                                        setDealObject({ ...dealObject, status: value });
                                    }
                                }}
                                disabled={dealObject?.toBe === DEAL_WON}
                            />
                        </div>
                    </div>

                    <ExtensionInputGroup
                        label="Campaign Type"
                        type="select"
                        options={campaignTypesOptions}
                        onChange={(selected) => {
                            setDealObject({ ...dealObject, dealCampaignType: selected.value });
                        }}
                        value={
                            dealObject.dealCampaignType
                                ? { label: dealObject.dealCampaignType, value: dealObject.dealCampaignType }
                                : ""
                        }
                        placeholder="Select Campaign Type"
                        disabled={dealObject?.createAnotherDeal}
                    />
                </>
            )}

            {publicTypes.includes(dealObject.dealCampaignType) && (
                <ExtensionNewPublicDeal
                    dealObject={dealObject}
                    setDealObject={setDealObject}
                    validateField={validateField}
                />
            )}
            {(dealObject.dealCampaignType === "EdTech" ||
                dealObject.dealCampaignType === "Consulting" ||
                dealObject.dealCampaignType === "E-Learning" ||
                dealObject.dealCampaignType === "Coaching") && (
                <ExtensionConsultingDeal
                    dealObject={dealObject}
                    setDealObject={setDealObject}
                    validateField={validateField}
                />
            )}
            {(dealObject.dealCampaignType === "In-house" ||
                dealObject.dealCampaignType === "LVT In-house" ||
                dealObject.dealCampaignType === "Learning Journey") && (
                <ExtensionNewInhouseDeal
                    dealObject={dealObject}
                    setDealObject={setDealObject}
                    validateField={validateField}
                />
            )}
        </div>
    );
});

export default ExtensionNewDeal;
